import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { PALETTE, PINES, Pine, GreenhousePlants, Decor, Weather, Kit } from "./sceneSeason";
import { HOLIDAYS, HolidaySky, HolidayGround } from "./sceneHoliday";
import { WX_SKY, WeatherSky, WeatherFront } from "./sceneWeather";
import { skyAt, mix, mixAll, NIGHT_SKY } from "./sceneSky";
import { motion, useReducedMotion } from "framer-motion";

const ease = [0.32, 0.72, 0, 1];
const G = 150;
const SKIN = "#F3CBA5";
const BLONDE = "#F2D06B";

const loop = (count, rest, a, b, fmt) => {
  const total = count + rest;
  const vals = [], keys = [];
  for (let i = 0; i < count; i++) { vals.push(fmt(a), fmt(b)); keys.push(i / total, (i + 0.5) / total); }
  vals.push(fmt(a), fmt(a)); keys.push(count / total, 1);
  return { values: vals.join(";"), keyTimes: keys.map((k) => k.toFixed(4)).join(";"), total };
};
// Gestures play in bursts, rest, then repeat forever so the scene stays alive without restarting.
const spin = (reduce, cx, cy, a, b, dur, begin, count = 4) => {
  if (reduce) return null;
  const rest = Math.max(2, Math.round(count * 0.75)) + ((begin * 10) % 3);
  const l = loop(count, rest, a, b, (v) => `${v} ${cx} ${cy}`);
  return <animateTransform attributeName="transform" type="rotate" values={l.values} keyTimes={l.keyTimes} dur={`${(l.total * dur).toFixed(2)}s`} begin={`${begin}s`} repeatCount="indefinite" />;
};

export function Kid({ x, girl, reduce, d, season }) {
  return (
    <g transform={`translate(${x} ${G})`} data-testid={girl ? "scene-girl" : "scene-boy"}>
      <rect x="-5" y="-14" width="4" height="14" rx="2" fill="#3A5A8C" /><rect x="1" y="-14" width="4" height="14" rx="2" fill="#3A5A8C" />
      {girl ? <path d="M-8 -14 L-5 -32 H5 L8 -14 Z" fill="#F7A8C4" /> : <rect x="-6.5" y="-32" width="13" height="19" rx="4" fill="#E0242B" />}
      <g>{spin(reduce, girl ? -5 : 5, -29, 0, girl ? 30 : -30, 0.7, d)}
        <path d={girl ? "M-5 -29 L-14 -44" : "M5 -29 L14 -44"} stroke={SKIN} strokeWidth="3.5" strokeLinecap="round" />
        <circle cx={girl ? -14 : 14} cy="-45" r="2.6" fill={SKIN} />
      </g>
      <path d={girl ? "M5 -29 L9 -19" : "M-5 -29 L-9 -18"} stroke={SKIN} strokeWidth="3.5" strokeLinecap="round" />
      {!girl && <g data-testid="scene-lego">
        {[["#0A84FF", -12], ["#FFD60A", -11.2], ["#E0242B", -12.4], ["#30D158", -11.6]].map(([c, bx], i) => { const y = -21.4 - i * 3.3; return (
          <g key={c}><rect x={bx} y={y} width="6.4" height="3.3" rx="0.6" fill={c} /><rect x={bx + 0.9} y={y - 0.9} width="1.6" height="1" rx="0.3" fill={c} /><rect x={bx + 3.9} y={y - 0.9} width="1.6" height="1" rx="0.3" fill={c} /><rect x={bx} y={y + 2.5} width="6.4" height="0.8" fill="#000" opacity="0.12" /></g>
        ); })}
        <circle cx="-9" cy="-18" r="2.6" fill={SKIN} />
      </g>}
      {girl && <g data-testid="scene-doll"><rect x="7.5" y="-20" width="6" height="8" rx="2" fill="#7FB3E6" /><circle cx="10.5" cy="-22.5" r="2.6" fill={SKIN} /><path d="M8 -24 Q10.5 -27 13 -24" stroke="#8A5A2B" strokeWidth="1.6" fill="none" /></g>}
      <circle cx="0" cy="-39" r="7.5" fill={SKIN} />
      {girl ? (
        [[-7, -42], [-5, -46.5], [0, -48], [5, -46.5], [7, -42], [-8, -37], [8, -37], [-7.5, -32.5], [7.5, -32.5]].map(([cx, cy], i) => <circle key={i} cx={cx} cy={cy} r="3.4" fill={BLONDE} />)
      ) : (
        <path d="M-7.5 -40 Q-8 -48 0 -48 Q8 -48 7.5 -41 Q3 -44 -2 -43 Z" fill={BLONDE} />
      )}
      <Kit season={season} />
      {!girl && <g stroke="#E0242B" strokeWidth="1.4" fill="none" data-testid="scene-glasses"><circle cx="-3" cy="-39" r="2.4" /><circle cx="3.4" cy="-39" r="2.4" /><path d="M-0.6 -39 H1" /></g>}
      <circle cx="-2.6" cy="-39" r="0.9" fill="#2B2B2B" /><circle cx="3.2" cy="-39" r="0.9" fill="#2B2B2B" />
      <path d="M-2 -35.5 Q0.3 -33.8 2.6 -35.5" stroke="#B5523B" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </g>
  );
}

function Dog({ x, color, ear, reduce, d, testid }) {
  return (
    <g transform={`translate(${x} ${G})`} data-testid={testid}>
      <path d="M13 -16 Q20 -24 22 -18" stroke={color} strokeWidth="3.4" fill="none" strokeLinecap="round">{spin(reduce, 13, -16, -18, 22, 0.36, d, 8)}</path>
      {[-10, -5, 6, 11].map((lx) => <rect key={lx} x={lx} y="-9" width="3.6" height="9" rx="1.6" fill={color} />)}
      <ellipse cx="1" cy="-13" rx="14" ry="7" fill={color} />
      <g>{spin(reduce, -9, -16, 0, -7, 1, d + 0.2)}
        <circle cx="-13" cy="-21" r="6.5" fill={color} />
        <ellipse cx="-19" cy="-19" rx="4.5" ry="3.2" fill={color} />
        <circle cx="-22.6" cy="-19.6" r="1.4" fill="#2B2B2B" />
        <circle cx="-14.5" cy="-23" r="1" fill="#2B2B2B" />
        <path d="M-10 -26 Q-6 -24 -8 -16" fill={ear} />
        <path d="M-20 -16.5 Q-19 -13.5 -17 -15.5" fill="#E86A7A" />
      </g>
    </g>
  );
}

function Chicken({ x, reduce, d }) {
  return (
    <g transform={`translate(${x} ${G})`} data-testid="scene-chicken">
      <path d="M-2 -6 V0 M2 -6 V0" stroke="#E8A100" strokeWidth="1.4" />
      <path d="M6 -12 Q11 -18 9 -8 Z" fill="#7D838A" />
      <ellipse cx="0" cy="-10" rx="7.5" ry="5.5" fill="#9AA0A6" />
      <g>{spin(reduce, -3, -13, 0, -40, 0.9, d)}
        <circle cx="-6" cy="-17" r="3.6" fill="#9AA0A6" />
        <path d="M-8 -21 q1 -2 2 0 q1 -2 2 0 v1 h-4 Z" fill="#E0242B" />
        <path d="M-9.4 -17.4 L-12.4 -16.4 L-9.4 -15.6 Z" fill="#E8A100" />
        <circle cx="-6.8" cy="-17.6" r="0.7" fill="#2B2B2B" />
      </g>
    </g>
  );
}

function Duck({ x, reduce, d }) {
  return (
    <g transform={`translate(${x} ${G})`} data-testid="scene-duck">
      <g>{spin(reduce, 0, 0, -6, 6, 0.8, d)}
        <path d="M-2 -4 V0 h-2 M2 -4 V0 h-2" stroke="#F08A24" strokeWidth="1.4" fill="none" />
        <ellipse cx="0" cy="-8" rx="8" ry="5" fill="#FBFBF7" stroke="#E5E2D6" strokeWidth="0.8" />
        <path d="M7 -10 L10 -13 L8 -7 Z" fill="#FBFBF7" />
        <circle cx="-6" cy="-15" r="3.6" fill="#FBFBF7" stroke="#E5E2D6" strokeWidth="0.8" />
        <path d="M-9.3 -15.6 L-13.6 -14.6 L-9.3 -13.2 Z" fill="#F08A24" />
        <circle cx="-6.6" cy="-15.8" r="0.7" fill="#2B2B2B" />
      </g>
    </g>
  );
}


export function Puppy({ x, reduce }) {
  const c = "#E9BC6A";
  return (
    <g transform={`translate(${x} ${G})`} data-testid="scene-golden">
      <g>
        {!reduce && <animateTransform attributeName="transform" type="translate" {...(({ values, keyTimes }) => ({ values, keyTimes }))(loop(6, 5, 0, -3, (v) => `0 ${v}`))} dur="5.5s" begin="0.4s" repeatCount="indefinite" />}
        <path d="M8 -9 Q14 -15 15 -10" stroke={c} strokeWidth="2.6" fill="none" strokeLinecap="round">{spin(reduce, 8, -9, -22, 26, 0.22, 0.2, 16)}</path>
        {[-6, -2.5, 3, 6.5].map((lx) => <rect key={lx} x={lx} y="-5" width="2.8" height="5" rx="1.3" fill={c} />)}
        <ellipse cx="0.5" cy="-8" rx="9" ry="5" fill={c} />
        <g>{spin(reduce, -6, -11, 0, -10, 0.8, 0.3, 4)}
          <circle cx="-8.5" cy="-14.5" r="5.8" fill={c} />
          <ellipse cx="-13.4" cy="-12.6" rx="3.2" ry="2.4" fill="#F3D199" />
          <circle cx="-15.8" cy="-13.2" r="1.1" fill="#2B2B2B" />
          <circle cx="-9.8" cy="-16" r="0.95" fill="#2B2B2B" />
          <path d="M-5.5 -19 Q-1.5 -17 -3.4 -10.5" fill="#D29A45" />
          <path d="M-14.2 -10.6 Q-13.4 -8 -11.8 -9.8" fill="#E86A7A" />
        </g>
      </g>
    </g>
  );
}

function Coop({ x }) {
  return (
    <g transform={`translate(${x} ${G})`} data-testid="scene-coop">
      <rect x="-1" y="-4" width="2" height="4" fill="#7A3B2E" /><rect x="33" y="-4" width="2" height="4" fill="#7A3B2E" />
      <rect x="0" y="-26" width="34" height="22" rx="1.5" fill="#C8352B" />
      <path d="M-3 -25 L17 -38 L37 -25 Z" fill="#8E2A22" />
      <path d="M-3 -25 L17 -38 L37 -25" stroke="#fff" strokeWidth="1.3" fill="none" strokeLinejoin="round" />
      <rect x="0" y="-26" width="34" height="22" rx="1.5" fill="none" stroke="#fff" strokeWidth="1.2" />
      <rect x="12" y="-18" width="10" height="14" rx="1" fill="#5A1E18" />
      <path d="M12 -18 L22 -4 M22 -18 L12 -4" stroke="#fff" strokeWidth="0.9" strokeOpacity="0.8" />
      <rect x="3" y="-22" width="6" height="5" rx="0.8" fill="#FFE9A8" stroke="#fff" strokeWidth="0.8" />
      <path d="M22 -4 L32 0 H22 Z" fill="#A8783F" />
    </g>
  );
}

function Cloud({ x, y, s = 1, reduce, drift, fill = "#fff" }) {
  return (
    <g data-testid="scene-cloud">
      {!reduce && <animateTransform attributeName="transform" type="translate" values={`0 0;${drift} 0;0 0`} dur="26s" repeatCount="indefinite" />}
      <g transform={`translate(${x} ${y}) scale(${s})`} fill={fill}>
        <ellipse cx="0" cy="0" rx="13" ry="5.5" />
        <circle cx="-5" cy="-3.5" r="5.5" /><circle cx="3" cy="-5.5" r="7" /><circle cx="9.5" cy="-2" r="4.5" />
      </g>
    </g>
  );
}


const BAND = 196;
const viewBox = (w, h, wide, band) => {
  if (band) { const vh = 235, s = h / vh, vw = w / s; return { x: Math.min(265 - vw / 2, 398 - vw), y: G - vh * 0.72, vw, vh }; }
  if (wide) { const s = Math.min((0.6 * w) / 400, (0.82 * h) / 125); return { x: 424 - w / s, y: 168 - h / s, vw: w / s, vh: h / s }; }
  const s = Math.min(w / 432, 1.3);
  return { x: 208 - w / s / 2, y: 168 - BAND / s, vw: w / s, vh: h / s };
};
const CLOUDS = [[-230, 0.18, 1.1, 12], [-90, 0.42, 0.8, -10], [40, 0.3, 0.9, 10], [150, 0.5, 1.1, 14], [240, 0.22, 0.8, -10], [330, 0.48, 0.65, 8]];

export default function ArrivalScene({ name = "New plant", color = "#30A14E", still = false, theme, className = "", ambient = false, weather = null, band = false, clock = null, clockMinutes = null }) {
  const reduce = useReducedMotion();
  const skip = reduce || still || ambient;
  const uid = useId().replace(/:/g, "");
  const ref = useRef(null);
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const measure = () => setSize({ w: el.offsetWidth, h: el.offsetHeight, wide: !band && window.matchMedia("(min-width: 1024px)").matches });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [band]);
  const svgRef = useRef(null);
  const hasSvg = !!(size?.w && size?.h);
  // Phones: freeze the looping SVG once the welcome has played, and whenever it is off screen.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg?.pauseAnimations) return;
    let visible = true, done = false;
    const sync = () => (visible && !done && document.visibilityState !== "hidden" ? svg.unpauseAnimations() : svg.pauseAnimations());
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); });
    io.observe(svg);
    document.addEventListener("visibilitychange", sync);
    const t = !ambient && window.matchMedia("(pointer: coarse)").matches ? setTimeout(() => { done = true; sync(); }, 9000) : null;
    if (still && ambient) { done = true; sync(); }
    return () => { io.disconnect(); clearTimeout(t); document.removeEventListener("visibilitychange", sync); };
  }, [hasSvg]); // eslint-disable-line react-hooks/exhaustive-deps
  const { season, holiday } = theme;
  const hol = holiday ? HOLIDAYS[holiday] : {};
  const wx = weather === "partly" ? "sun" : weather;
  const cyc = clock ? skyAt(clock, PALETTE[season].sky, clockMinutes) : null;
  const wxPal = WX_SKY[wx] && !(hol.sky && (wx === "sun" || wx === "night")) ? WX_SKY[wx] : {};
  let pal = { ...PALETTE[season], ...(hol.sky ? { sky: hol.sky } : {}), ...(hol.cloud ? { cloud: hol.cloud } : {}), ...wxPal };
  if (cyc) {
    const wxSky = WX_SKY[wx] && wx !== "night" ? WX_SKY[wx] : null;
    pal = { ...pal, sky: wxSky ? mixAll(wxSky.sky, NIGHT_SKY, cyc.night * 0.85) : cyc.sky, cloud: mix(wxSky?.cloud || PALETTE[season].cloud, "#46557F", cyc.night * 0.75), sun: mix(PALETTE[season].sun, "#FF8A3D", 1 - Math.max(0, cyc.elev)) };
  }
  const clear = !["rain", "snow", "fog", "cloud"].includes(wx);
  const night = cyc ? false : (hol.night && !wxPal.sky) || wx === "night";
  const sunOn = cyc ? cyc.elev > 0 && !["rain", "snow", "fog"].includes(wx) : !night && !["rain", "snow", "fog"].includes(wx);
  const sx = cyc ? 170 + 115 * Math.min(1, Math.max(0, cyc.prog)) : 250;
  const sy = (f) => (cyc ? top + (0.95 - 0.7 * Math.max(0, cyc.elev)) * (G - top) : top + f * (G - top));
  const clouds = weather === "cloud" || wx === "rain" || wx === "snow" ? [...CLOUDS, ...CLOUDS.map(([x, f, s, d]) => [x + 60, f * 0.7, s * 1.25, -d])] : CLOUDS;
  const b = size?.w && size?.h ? viewBox(size.w, size.h, size.wide, band) : null;
  const top = b ? b.y : 40;
  const at = (f) => top + f * (G - top);
  const arrive = skip ? { initial: false, animate: { x: 322 } } : { initial: { x: -60, opacity: 0 }, animate: { x: [-60, 150, 322], y: [0, -3, 0, -3, 0], opacity: [0, 1, 1] }, transition: { duration: 2.6, delay: 0.5, ease, times: [0, 0.55, 1] } };
  const door = (left) => (skip ? { initial: false } : { initial: { scaleX: 0 }, animate: { scaleX: 1 }, transition: { delay: 3.1, duration: 0.35, ease }, style: { transformBox: "fill-box", transformOrigin: left ? "0% 50%" : "100% 50%" } });
  const house = `M300 ${G} V92 L345 60 L390 92 V${G} Z`;
  const front = season === "winter" || season === "fall";
  return (
    <div ref={ref} className={`absolute inset-0 select-none overflow-hidden ${className}`} data-testid="arrival-scene" data-season={season} data-holiday={holiday || ""} role="img"
      aria-label={ambient ? `The Green House on a ${hol.label ? `${hol.label} ` : ""}${season} day${weather ? `, ${weather} weather` : ""}` : `${name} arrives at the Green House on a ${hol.label ? `${hol.label} ` : ""}${season} day while the family, puppy, dogs, chickens and ducks wave hello`}>
      {b && (
        <svg ref={svgRef} viewBox={`${b.x} ${b.y} ${b.vw} ${b.vh}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
          <defs>
            <linearGradient id={`arr-sky-${uid}`} gradientUnits="userSpaceOnUse" x1="0" y1={top} x2="0" y2={G}><stop offset="0" stopColor={pal.sky[0]} /><stop offset="0.6" stopColor={pal.sky[1]} /><stop offset="1" stopColor={pal.sky[2]} /></linearGradient>
            <radialGradient id={`arr-sun-${uid}`}><stop offset="0" stopColor="#FFF9DC" /><stop offset="0.55" stopColor={pal.sun} /><stop offset="1" stopColor={pal.sun} stopOpacity="0.9" /></radialGradient>
            <radialGradient id={`arr-glow-${uid}`}><stop offset="0" stopColor={pal.sun} stopOpacity={season === "winter" ? 0.35 : 0.6} /><stop offset="1" stopColor={pal.sun} stopOpacity="0" /></radialGradient>
            <linearGradient id={`arr-glass-${uid}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fff" stopOpacity="0.6" /><stop offset="1" stopColor={color} stopOpacity="0.16" /></linearGradient>
          </defs>
          <rect x={b.x - 10} y={top - 10} width={b.vw + 20} height={G - top + 10} fill={`url(#arr-sky-${uid})`} />
          {sunOn && <g data-testid="scene-sun" opacity={weather === "cloud" ? 0.45 : 1}>
            <circle cx={sx} cy={sy(0.3)} r="30" fill={`url(#arr-glow-${uid})`} />
            {season !== "winter" && <g stroke={pal.sun} strokeWidth="2" strokeLinecap="round" opacity="0.85">
              {!reduce && <animateTransform attributeName="transform" type="rotate" values={`0 ${sx} ${sy(0.3)};360 ${sx} ${sy(0.3)}`} dur="80s" repeatCount="indefinite" />}
              {Array.from({ length: 10 }, (_, i) => { const a = (i * Math.PI) / 5; return <path key={i} d={`M${sx + Math.cos(a) * 14} ${sy(0.3) + Math.sin(a) * 14} L${sx + Math.cos(a) * 19} ${sy(0.3) + Math.sin(a) * 19}`} />; })}
            </g>}
            <motion.circle cx={sx} cy={sy(0.3)} r="10" fill={`url(#arr-sun-${uid})`} initial={skip ? false : { scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: season === "winter" ? 0.85 : 1 }} transition={{ duration: 0.8 }} style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }} />
          </g>}
          {cyc ? <g className="[&_[data-testid=scene-moon]]:hidden [&_[data-testid=scene-stars]]:hidden"><HolidaySky holiday={holiday} top={top} reduce={reduce} /></g> : <HolidaySky holiday={holiday} top={top} reduce={reduce} />}
          {cyc ? (clear && cyc.night > 0.05 && <g opacity={cyc.night} data-testid="scene-night-sky"><WeatherSky wx="night" top={top} /></g>) : !hol.night && <WeatherSky wx={wx} top={top} />}
          {clouds.map(([x, f, s, drift], i) => <Cloud key={i} x={x} y={at(f)} s={s} reduce={reduce || (still && ambient)} drift={drift} fill={pal.cloud} />)}
          {!front && <Weather season={season} reduce={reduce} top={top} />}
          <path d="M-700 150 C-520 118 -380 140 -240 126 S-40 118 120 134 S320 120 520 132 S700 124 760 140 V150 Z" fill={pal.hill} />
          {PINES.map(([x, h]) => <Pine key={x} x={x} h={h} snow={season === "winter"} tone={pal.pine} />)}
          <rect x={b.x - 10} y={G - 2} width={b.vw + 20} height={b.y + b.vh - G + 12} fill={pal.ground} />
          <path d={`M${b.x - 10} ${G} H${b.x + b.vw + 10}`} stroke={pal.edge} strokeWidth="3" strokeLinecap="round" />

          {cyc && cyc.night > 0 && <rect x={b.x - 10} y={top - 10} width={b.vw + 20} height={b.vh + 20} fill="#0B1433" opacity={cyc.night * 0.32} data-testid="scene-night-tint" />}
          <motion.g initial={skip ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} data-testid="scene-greenhouse">
            <path d={house} fill={`url(#arr-glass-${uid})`} />
            {cyc && cyc.night > 0 && <path d={house} fill="#FFC861" opacity={cyc.night * 0.85} data-testid="scene-greenhouse-glow" />}
            <GreenhousePlants />
            <path d={house} fill="#fff" fillOpacity="0.14" stroke={color} strokeWidth="3" strokeLinejoin="round" />
            <path d={`M345 60 V116 M300 92 H390 M322 76 V${G} M368 76 V${G}`} stroke={color} strokeOpacity="0.35" strokeWidth="1.6" />
            <rect x="334" y="116" width="22" height={G - 116} rx="2" fill="hsl(var(--background))" stroke={color} strokeWidth="2.4" />
          </motion.g>

          <g data-testid="scene-address" transform="translate(390 0)">
            <rect x="18.5" y="136" width="3" height={G - 136} fill="#8E8E93" />
            <motion.g initial={skip ? false : { rotate: -8, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 12, delay: 0.25 }} style={{ transformBox: "fill-box", transformOrigin: "50% 100%" }}>
              <rect x="12" y="92" width="16" height="45" rx="2.5" fill="#1D5FD1" />
              <rect x="13.3" y="93.3" width="13.4" height="42.4" rx="1.8" fill="none" stroke="#fff" strokeOpacity="0.9" strokeWidth="0.8" />
              {"29302".split("").map((ch, i) => <text key={i} x="20" y={101.6 + i * 8.1} textAnchor="middle" fontSize="7.6" fontWeight="800" fill="#fff" fontFamily="inherit">{ch}</text>)}
            </motion.g>
          </g>

          {!ambient && <>
          <Coop x={80} />
          <Duck x={44} reduce={reduce} d={0.2} />{holiday !== "thanksgiving" && <Duck x={62} reduce={reduce} d={0.35} />}
          <Chicken x={92} reduce={reduce} d={0.3} />{!["christmas", "thanksgiving", "halloween", "easter"].includes(holiday) && <Chicken x={118} reduce={reduce} d={0.6} />}
          <Kid x={140} girl reduce={reduce} d={0.6} season={season} />
          <Kid x={164} reduce={reduce} d={0.45} season={season} />
          <Puppy x={196} reduce={reduce} />
          <Dog x={234} color="#5C3A21" ear="#47301B" reduce={reduce} d={0.35} testid="scene-lab-1" />
          <Dog x={272} color="#6B4428" ear="#4E321D" reduce={reduce} d={0.5} testid="scene-lab-2" />
          </>}
          <Decor season={season} reduce={reduce} october={holiday === "halloween"} wreath={holiday !== "valentine"} />
          <HolidayGround holiday={holiday} reduce={reduce} />

          {!ambient && <motion.g {...arrive} data-testid="scene-plant">
            <path d={`M14 ${G} h16 l-2 -13 h-12 Z`} fill="#C8754B" />
            <path d={`M22 ${G - 13} C22 ${G - 25} 18 ${G - 31} 12 ${G - 33} C18 ${G - 33} 22 ${G - 29} 22 ${G - 23} C22 ${G - 31} 26 ${G - 37} 34 ${G - 37} C28 ${G - 33} 24 ${G - 27} 24 ${G - 13} Z`} fill={color} />
          </motion.g>}

          <motion.rect x="334" y="116" width="11" height={G - 116} rx="1.5" fill={color} fillOpacity="0.85" {...door(true)} />
          <motion.rect x="345" y="116" width="11" height={G - 116} rx="1.5" fill={color} fillOpacity="0.85" {...door(false)} />
          {!skip && <motion.path d={house} fill={color} initial={{ opacity: 0 }} animate={{ opacity: [0, 0.26, 0.07] }} transition={{ delay: 3.4, duration: 0.9 }} />}
          {!skip && [[318, 56], [374, 58], [394, 104], [298, 104], [345, 46]].map(([x, y], i) => (
            <motion.path key={i} d={`M${x} ${y - 5} L${x + 1.4} ${y - 1.4} L${x + 5} ${y} L${x + 1.4} ${y + 1.4} L${x} ${y + 5} L${x - 1.4} ${y + 1.4} L${x - 5} ${y} L${x - 1.4} ${y - 1.4} Z`} fill="#FFD60A"
              initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.25, 0], opacity: [0, 1, 0] }} transition={{ delay: 3.5 + i * 0.1, duration: 0.8 }} style={{ transformBox: "fill-box", transformOrigin: "50% 50%" }} />
          ))}
          {front && <Weather season={season} reduce={reduce} top={top} />}
          <WeatherFront wx={wx} top={top} reduce={reduce || (still && ambient)} />
        </svg>
      )}
    </div>
  );
}
